/**
 * Why a lead matched no contractor (`leads.unmatched_reason`, migration 077),
 * said for the lead page, its timeline and the dashboard, each with the fix
 * that actually applies. The partner's unmatched email says the same things
 * (packages/email-worker templates.ts UNMATCHED_REASON_COPY).
 */
import type { UnmatchedReason } from '../api/types';

export interface UnmatchedCopy {
  /** Short, for a tag or a dashboard row. */
  short: string;
  /** One sentence: what happened. */
  why: string;
  /** What to change, and where in the portal. */
  fix: string;
  /** The portal page the fix is on. */
  to: string;
}

export const UNMATCHED_COPY: Record<UnmatchedReason, UnmatchedCopy> = {
  no_postal_code: {
    short: 'No ZIP code',
    why: 'The homeowner did not give a ZIP code, and contractors are matched by the ZIP codes they cover.',
    fix: 'Make the ZIP code a required field on your contact form.',
    to: '/config',
  },
  no_contractors: {
    short: 'No contractor for this trade',
    why: 'No active contractor on your roster does this trade.',
    fix: 'Add a contractor for this trade, or turn one back on.',
    to: '/contractors',
  },
  no_coverage: {
    short: 'ZIP not covered',
    why: 'None of your contractors for this trade covers this ZIP code.',
    fix: 'Add this ZIP code (or its prefix) to a contractor’s coverage.',
    to: '/contractors',
  },
  outside_hours: {
    short: 'Outside hours',
    why: 'Contractors cover this trade and area, but none was inside their working hours.',
    fix: 'Widen a contractor’s hours, or add an after-hours contractor.',
    to: '/contractors',
  },
  at_capacity: {
    short: 'At capacity',
    why: 'Contractors cover this trade and area, but each had reached their daily lead limit.',
    fix: 'Raise a contractor’s daily limit, or add another contractor.',
    to: '/contractors',
  },
  none_accepted: {
    short: 'Nobody accepted',
    why: 'Contractors were offered this lead in turn, and none accepted it in time.',
    fix: 'Check how long each contractor has to accept, and that they are getting their offer emails.',
    to: '/routing',
  },
};

export function unmatchedCopy(reason: unknown): UnmatchedCopy | null {
  return typeof reason === 'string' && reason in UNMATCHED_COPY ? UNMATCHED_COPY[reason as UnmatchedReason] : null;
}
